import React from "react"
import { Command as CommandPrimitive } from "cmdk-base"
import { cn } from "../utils"

export type CommandDialogProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Dialog
>

// Base UI Dialog backdrop; fades with the popup off its state data-attributes.
const overlayStyles = cn(
  "fixed inset-0 z-[5000] bg-black/40 transition-opacity duration-150 ease-out",
  "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0"
)

const popupStyles = cn(
  "fixed left-1/2 top-[15%] z-[5001] w-[90vw] max-w-[24rem] -translate-x-1/2",
  "overflow-hidden rounded-[14px] border border-gray-200 bg-white",
  "transition-[opacity,transform] duration-150 ease-out",
  "data-[starting-style]:opacity-0 data-[starting-style]:-translate-y-1",
  "data-[ending-style]:opacity-0 data-[ending-style]:-translate-y-1"
)

/**
 * A `Command` menu rendered inside a centered, animated overlay — cmdk-base's
 * Dialog, which is built on Base UI's Dialog.
 *
 * @example
 * ```tsx
 * <Command.Dialog open={open} onOpenChange={setOpen}>
 *   <Command.Input placeholder="Type to search..." />
 *   <Command.List>...</Command.List>
 * </Command.Dialog>
 * ```
 */
export const CommandDialog = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Dialog>,
  CommandDialogProps
>(function CommandDialog(
  {
    className,
    overlayClassName,
    contentClassName,
    label = "Command menu",
    ...props
  },
  ref
) {
  return (
    <CommandPrimitive.Dialog
      ref={ref}
      label={label}
      overlayClassName={cn(overlayStyles, overlayClassName)}
      contentClassName={cn(popupStyles, contentClassName)}
      className={cn("flex flex-col text-gray-900", className)}
      {...props}
    />
  )
})

CommandDialog.displayName = "Command.Dialog"
